import { useContext } from 'react';
import { createGlobalStyle } from 'styled-components';
import { ProductsContext } from 'context-providers/products-provider';
import { ds01QuizHero } from './content/hero.content';
import { UgcHeadline, items } from './content/ugc.content';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { capsuleLarge, capsuleSmall } from './content/via-cap.content';
import { bookendItems } from './content/bookend.content';
import { content } from './content/via-cap.content';
import {
  microbiomeChapters,
  microbiomeImageSlide,
} from './content/micro-biome.content';
import { npdProductContent } from './content/product-carousel.content';
import {
  videos,
  ReviewCarouselHeadline,
} from './content/review-carousel.content';
import Bookends from 'components/library/Bookend';
import Loader from 'components/library/Loader';
import { UgcScroller } from 'components/library/Ugc';

import ViaCap from 'components/library/via-cap';
import ReviewCarousel from 'components/library/ReviewCarousel';
import Microbiome from 'components/library/MicroBiome';
import Head from 'next/head';
import HomeLocale from 'locale/en/home.json';
import ProductGrid from 'components/library/ProductCarousel/product-grid';
import Highlight from 'components/library/Highlight';
import { HighlightBackground } from 'components/library/Highlight/highlight.styled';
import { getMetadataTags } from 'lib/utils/get-metadata-tags';
import { dsDuoItems } from 'components/library/Highlight/highlight.content';
import { useActiveMiniSale } from 'hooks/use-active-mini-sale';

const DS01_CAROUSEL_URL = '/daily-synbiotic';

const GlobalHomepageStyles = createGlobalStyle`
  main {
    overflow: visible !important;
    margin-top: var(--flag-margin);
  }

  /* White-theme nav over the dark hero */
  body {
    --nav-ink: var(--fill-primary);
    --nav-logo: var(--fill-bright);
    --nav-cta-background: var(--fill-primary);
    --nav-cta-ink: var(--fill-brand);
  }
`;

const PageData = {
  page_type: 'default',
  page_class: 'page-home',
  page_name: HomeLocale.page_name,
  page_title: HomeLocale.page_title,
  page_description: HomeLocale.page_desc,
};

const Home = () => {
  const isSmall = useClientSideMediaQuery(960);
  const miniSale = useActiveMiniSale();
  const { getFormattedProductPrice } = useContext(ProductsContext);

  const carouselItems = npdProductContent.items.map((item) => {
    const price = item.sku ? getFormattedProductPrice(item.sku) : undefined;
    return {
      ...item,
      ...(price && {
        note: `Starting at ${price}`,
        desktopNote: `Starting at ${price} per month`,
      }),
      ...(miniSale &&
        item.primaryButton.url === DS01_CAROUSEL_URL && {
          label: miniSale.saleBadgeText,
        }),
    };
  });

  return (
    <>
      <GlobalHomepageStyles />
      <Head>
        <title>{PageData.page_title}</title>
        {getMetadataTags({
          pageDescription: PageData.page_description,
          canonicalUrl: 'https://seed.com',
          pageTitle: PageData.page_title,
          imageUrl:
            'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517361/structured-data/og-homepage.png',
          ogImageAlt: PageData.page_title,
        })}
      </Head>
      <Loader slides={ds01QuizHero} id={'homepage-ds01-hero'} />

      <ProductGrid
        id={'homepage-product-carousel-control'}
        title={npdProductContent.title}
        body={npdProductContent.body}
        items={carouselItems}
        primaryButton={npdProductContent.primaryButton}
        align={'left'}
      />

      <HighlightBackground id={'homepage-product-highlight-control'}>
        <Highlight
          borderRadius='var(--radius-xl) var(--radius-xl) 0 0'
          label={'Bundle + Save 25%'}
          title={'Daily essentials for nutrition and digestive health.'}
          body={
            'Our clinically studied daily synbiotic paired with a daily multivitamin reduces bloating, promotes healthy regularity and helps cover nutrient gaps.'
          }
          primaryButton={{
            label: 'Shop Daily Essentials Duo',
            url: '/daily-essentials-duo',
          }}
          items={dsDuoItems}
          variant='right'
          applyTransform={false}
          blockSize='auto'
        />
      </HighlightBackground>

      <ViaCap
        eyebrow={content.eyebrow}
        description={content.description}
        background={content.background}
        backgroundVideo={content.backgroundVideo}
        note={content.note}
        footnote={content.footnote}
        capsule={isSmall ? capsuleSmall : capsuleLarge}
      />

      <Microbiome chapters={[...microbiomeChapters, microbiomeImageSlide]} />

      <ReviewCarousel headline={<ReviewCarouselHeadline />} videos={videos} />
      <UgcScroller headline={<UgcHeadline />} items={items} type='simple' />

      <Bookends items={bookendItems} />
    </>
  );
};

export default Home;
